import {
  Box,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {CartItemList, Cutlery, OrderRecommendationList, SpecialMentions} from 'components/pages/cart';
import {colors, useCartContext, useCustomerContext, useOrderContext} from 'components/providers';
import {DeliveryCostInfo, OrderTotalDetails, Price} from 'components/shared';
import {CartPromoText} from 'components/shared/CartPromoText';
import {useDesktop, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC} from 'react';
import {ICartItem, IProduct} from 'types';
import ImageUI from 'ui/ImageUI';
import {getDeliveryTimeString} from 'utils/helpers';
import {v4 as uuidv4} from 'uuid';

import {CartItemsMetro} from '../../../../../../metro/components/cart';
import {useMetroStockValidation} from '../../../../../../metro/hooks/useMetroStockValidation';

interface Props {
  onOpenAlert: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const CartMenu: FC<Props> = ({onOpenAlert, isOpen, onClose}) => {
  const {
    cartItems,
    addProductToCart,
    changeCartItemQuantity,
    cartItemsPrice,
    deliveryDetails,
    cartProvider,
    editCartItem,
    deleteProduct,
    requiredProducts,
    restToFreeDelivery,
    dynamicDeliveryPrice,
  } = useCartContext();
  const {customer} = useCustomerContext();
  const isEmptyCart = !cartItems.length;
  const isNotEnoughForFreeDelivery = restToFreeDelivery > 0;
  const isFifthUpcomingFree = customer?.month_order_count === customer?.each_n_free - 1;
  const hasMinimalOrder = customer?.minimal_order_sum !== 0 && customer?.minimal_order_sum !== null;
  const minimalDiff = (customer?.minimal_order_sum ?? 0) - (cartItemsPrice ?? 0);
  const shouldHideForFifthFreeByMinOrder = Boolean(isFifthUpcomingFree && hasMinimalOrder && minimalDiff <= 0);

  const showFreeDeliveryBanner = isNotEnoughForFreeDelivery && !isEmptyCart && !shouldHideForFifthFreeByMinOrder;

  useMetroStockValidation();

  const {push, pathname} = useRouter();
  const isDesktop = useDesktop();
  const {t} = useTranslation();
  const {cutleryNumber, instructions, setCutleryNumber, setInstructions} = useOrderContext();

  const isOnCheckoutPage = pathname === '/checkout';

  const handleNextStep = () => {
    if (isOnCheckoutPage) {
      onClose();
    } else {
      push('/checkout');
    }
  };

  const handleRecommendedItemClick = (product: IProduct, cart?: ICartItem) => {
    const cartItem: ICartItem = {
      id: uuidv4(),
      product,
      productId: product.id,
      price: product.price,
      addedAutomatically: cart?.addedAutomatically ?? false,
      quantity: cart?.quantity ?? 1,
      instructions: cart?.instructions ?? '',
      selectedOptions: cart?.selectedOptions,
      added_from: 'checkout_recommended',
    };

    addProductToCart(cartItem);
  };
  const handleItemQuantityChange = (quantity: number, meta: {cartItemId: string}) => {
    changeCartItemQuantity(meta.cartItemId, quantity);
  };

  const handleCutleryNumber = (number: number) => {
    setCutleryNumber(number);
  };

  const handleSpecialMentionsChange = (text: string) => {
    setInstructions(text);
  };

  const deliveryTime = getDeliveryTimeString(cartProvider?.avg_delivery_time);
  const isMetro = cartProvider?.slug === 'metro';

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxW="700px" display="flex" flexDirection="column">
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px solid" borderColor={colors.borderGray}>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex alignItems="center">
                <ImageUI src={cartProvider?.logo_thumbnail} alt="icon" w="40px" h="40px" borderRadius="10px" />
                <Text variant="Text.baseStyle" fontWeight="bold" ml="15px">
                  {cartProvider?.name}
                </Text>
              </Flex>
              <Button variant="outline" minH="40px" mr="40px" onClick={onOpenAlert}>
                {t('CLEAR')}
              </Button>
            </Flex>
          </DrawerHeader>
          <DrawerBody bg={colors.lightGray} display="flex" flexDirection="column" flex="1">
            <Box flex="1" display="flex" flexDirection="column">
              <Box overflowY="auto" flex="1">
                {isMetro ? (
                  <Container variant="white-block">
                    <CartItemsMetro />
                  </Container>
                ) : (
                  <CartItemList
                    items={cartItems}
                    onItemEdit={editCartItem}
                    onQuantityChange={handleItemQuantityChange}
                    onItemDelete={deleteProduct}
                  />
                )}
                <Box mt={['10px', '30px']}>
                  {requiredProducts.map((item, index) => (
                    <Container key={index} variant="white-block" w="100%" position="relative" mt={['10px', '30px']}>
                      <Flex w="100%" alignItems="center">
                        <Box minW={['70px', '120px']} w={['70px', '120px']} h={['70px', '120px']}>
                          <ImageUI
                            w="100%"
                            mr="30px"
                            mt="15px"
                            borderRadius="1rem"
                            src={item.product_img_for_list_thumbnail || '/food_pattern.webp'}
                            alt={`${item.name} image`}
                            fallbacksrc="/food_pattern.webp"
                          />
                        </Box>
                        <VStack
                          color={colors.lightBlack}
                          flexDirection="row"
                          w="100%"
                          ml="30px"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={['15px', '18px']}
                        >
                          <Text fontWeight="600" fontSize={['14px', '16px']}>
                            {item.name}
                          </Text>
                          <Price
                            value={item.price}
                            priceProps={{
                              fontSize: isDesktop ? '16px' : '14px',
                              fontWeight: '600',
                            }}
                          />
                        </VStack>
                      </Flex>
                    </Container>
                  ))}
                </Box>
              </Box>
              <Box mb="30px">
                {cartProvider?.recommended_cart_products && (
                  <OrderRecommendationList
                    products={cartProvider?.recommended_cart_products}
                    onItemClick={handleRecommendedItemClick}
                    title={t('COMPLETE_ORDER')}
                  />
                )}
              </Box>
              <Stack direction="column" spacing={['15px', '30px']}>
                {cartProvider?.provides_cutlery && !isMetro && (
                  <Cutlery
                    title={t('CUTLERY')}
                    description={t('CUTLERY_DESCRIPTION')}
                    value={cutleryNumber}
                    onChange={handleCutleryNumber}
                  />
                )}
                <SpecialMentions
                  value={instructions}
                  onChange={handleSpecialMentionsChange}
                  title={t('SPECIAL_MENTIONS')}
                />
              </Stack>

              <Box mt="30px" borderTop="1px solid" borderColor={colors.borderGray}>
                <OrderTotalDetails
                  requiredProducts={requiredProducts}
                  cartItemsPrice={cartItemsPrice}
                  deliveryDetails={{
                    deliveryAmount: deliveryDetails?.deliveryAmount ?? 0,
                  }}
                  deliveryTime={deliveryTime}
                  t={t}
                  dynamicDeliveryPrice={dynamicDeliveryPrice}
                  difficult_delivery_surcharge={cartProvider?.base_delivery_cost_data?.difficult_delivery_surcharge}
                  difficult_delivery_reason={cartProvider?.base_delivery_cost_data?.difficult_delivery_reason}
                  nighttimeAddedTerms={cartProvider?.base_delivery_cost_data?.nighttime_added_terms}
                />
                <CartPromoText borderSide="top" />

                {showFreeDeliveryBanner && (
                  <Box pt="15px">
                    <Flex alignItems="center" bg={colors.white} borderRadius="16px" p="15px">
                      <DeliveryCostInfo />
                    </Flex>
                  </Box>
                )}
                <Flex w="full" justifyContent="center" alignItems="center" pb="80px">
                  <Box
                    w="94%"
                    borderRadius="20px"
                    cursor="pointer"
                    color={colors.white}
                    position="fixed"
                    bottom={1}
                    zIndex="overlay"
                  >
                    <Button
                      as="a"
                      cursor="pointer"
                      onClick={handleNextStep}
                      _focus={{border: 'none'}}
                      variant="primary"
                      h="50px"
                      w="full"
                      my="10px"
                    >
                      {isOnCheckoutPage ? t('CLOSE') : t('NEXT_STEP')}
                    </Button>
                  </Box>
                </Flex>
              </Box>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
